import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogActions, Slide, AppBar, Toolbar, IconButton, Typography, Paper, Box, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { v4 as uuidv4 } from 'uuid';
import { buildFilter } from '../utils/sqlUtils';
import fieldsDefinition from '../defaults/CustomFilterDefinitions.json';
import { setActiveFilter } from '../../../redux/slices/customFilterSlice';
import FieldNames from '../../../defaults/FieldNames.json';
import CustomFilterService from '../../../services/CustomFilterService';
import ErrorService from '../../../services/ErrorService';
import BasicTextField from '../../details/customPanels/BasicTextField';
import ArcgisFilter from './ArcgisFilter';
import ColorConstants from '../../../defaults/ColorConstants';
import { updateFilterExpression } from '../../../utils/ExpressionHelper';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function CustomFiltersDialog({open, onClose, filterForEdit, customSortedFilterListItems, 
  setCustomSortedFilterListItems, userInfo, filterTitleLocal, setFilterTitleLocal }) {
  const [filterState, setFilterState] = useState(filterForEdit);
  const [fields, setFields] = useState([]);
  const isUserPostfire = useMemo(() => {return userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes}, [userInfo]);
  const isUserFuels = useMemo(() => {return userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes}, [userInfo]);
  const [datasetSelection,setDatasetSelection] = useState(filterForEdit?.[FieldNames.customFilter.dataset] ?? ((isUserPostfire && !isUserFuels) ? "POSTFIRE" : "FUELS"));

  useEffect(() => {
    const fieldArray = Object.keys(fieldsDefinition).map(key => {
      return fieldsDefinition[key];
    });
    const sortedFields = fieldArray.sort((a, b) => {
      return a.alias < b.alias ? -1 : 1;
    });
    setFields(sortedFields);
  }, [])

  const dispatch = useDispatch();

  const getModifiedSets = (filter) => {
    Object.keys(filter[FieldNames.customFilter.sets]).forEach(key => {
      const set = filter[FieldNames.customFilter.sets][key];
      Object.keys(set.expressions).forEach(key => {
        const expression = set.expressions[key];
        if(expression?.operator){
          if(expression.operator.operator && (expression.operator.operator.includes('>') || expression.operator.operator.includes('<')))
          {
            const recreatedOperator = expression.operator.operator.replaceAll('<', '&lt;').replaceAll('>', '&gt;');
            expression.operator.operator = recreatedOperator;
          }
          if(expression.operator.alias === "nonop")
          {
            expression.field.expressionField = expression.field.expressionField?.replaceAll('<', '&lt;')?.replaceAll('>', '&gt;');
          }
        }
        return expression;
      });
      return set;
    });
    return filter[FieldNames.customFilter.sets];
  }

  const clearActive = (item, activeDefinitionExpression) => {
    let tempItems = [];
    let tempArray = [];
    customSortedFilterListItems?.forEach(eaFilter => {
      let tempEaFilter = JSON.parse(JSON.stringify(eaFilter));
      if(eaFilter[FieldNames.customFilter.userCustomFiltersID] !== item[FieldNames.customFilter.userCustomFiltersID]){
        tempEaFilter[FieldNames.customFilter.isActive] = FieldNames.yesNo.no;
        let filterToUpdate = {attributes: {}};
        filterToUpdate.attributes[FieldNames.customFilter.objectID] = eaFilter[FieldNames.customFilter.objectID];
        filterToUpdate.attributes[FieldNames.customFilter.userID] = eaFilter[FieldNames.customFilter.userID];
        filterToUpdate.attributes[FieldNames.customFilter.isActive] = FieldNames.yesNo.no;
        filterToUpdate.attributes[FieldNames.customFilter.userCustomFiltersID] = eaFilter[FieldNames.customFilter.userCustomFiltersID]; 
        tempArray.push(filterToUpdate);
        tempItems.push(tempEaFilter);
      }
    });
    
    if(tempArray.length > 0){
      CustomFilterService.updateCustomFilter(tempArray).then(response => {
        if(response && response.updateResults && response.updateResults.length > 0 && response.updateResults[0].success){
          let noItems = tempItems;
          noItems.push(item);
          let sortedAllItems = noItems.sort((a, b) => (a.title > b.title) ? 1 : -1);
          setCustomSortedFilterListItems(sortedAllItems);
          let temp = JSON.parse(JSON.stringify(item));
          dispatch(setActiveFilter(temp, activeDefinitionExpression));
          const finalStr = updateFilterExpression(userInfo);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      tempItems.push(item);
      dispatch(setActiveFilter(item, activeDefinitionExpression))
      const finalStr = updateFilterExpression(userInfo);
      setCustomSortedFilterListItems(tempItems);
    }
  }
  
  const saveFilter = () => {
    let activeDefinitionExpression = "";
    let saveableActiveDefinitionExpression = "";
    if(filterTitleLocal.trim() === '') {
      return;
    }
    if (filterForEdit !== null)
    {
      const filter = Object.assign({}, filterForEdit, filterState);
      //Edit
      if (filter[FieldNames.customFilter.title] !== filterTitleLocal && (filterTitleLocal !== ''))
      {
        filter[FieldNames.customFilter.title] = filterTitleLocal.replaceAll('&lt;', '<').replaceAll('&gt;', '>');
      }
      activeDefinitionExpression = buildFilter(filter, false, userInfo);
      saveableActiveDefinitionExpression = buildFilter(filter, true, userInfo)
      filter[FieldNames.customFilter.expression] = saveableActiveDefinitionExpression;
      filter[FieldNames.customFilter.isActive] = FieldNames.yesNo.yes;
      filter[FieldNames.customFilter.dataset] = datasetSelection;
      let modifiedSets = getModifiedSets(filter);
      
      let newItem = {attributes: {}};
      newItem.attributes[FieldNames.customFilter.objectID] = filter[FieldNames.customFilter.objectID];
      newItem.attributes[FieldNames.customFilter.userID] = filter[FieldNames.customFilter.userID];
      newItem.attributes[FieldNames.customFilter.mustMatchAll] = filter[FieldNames.customFilter.mustMatchAll] ? 'Yes' : 'No';
      newItem.attributes[FieldNames.customFilter.expression] = filter[FieldNames.customFilter.expression];
      newItem.attributes[FieldNames.customFilter.sets] = JSON.stringify(modifiedSets); 
      newItem.attributes[FieldNames.customFilter.title] = filter[FieldNames.customFilter.title] ? filter[FieldNames.customFilter.title]?.replaceAll('<', '&lt;')?.replaceAll('>', '&gt;') : '';
      newItem.attributes[FieldNames.customFilter.isActive] = FieldNames.yesNo.yes;
      newItem.attributes[FieldNames.customFilter.userCustomFiltersID] = filter[FieldNames.customFilter.userCustomFiltersID]; 
      newItem.attributes[FieldNames.customFilter.dataset] = datasetSelection;
      CustomFilterService.updateCustomFilter([newItem]).then(response => {
        if(response && response.updateResults && response.updateResults.length > 0 && response.updateResults[0].success){
          let currentCustomFilter = customSortedFilterListItems.filter(x => x[FieldNames.customFilter.userCustomFiltersID] === filter[FieldNames.customFilter.userCustomFiltersID]);
          if(currentCustomFilter && currentCustomFilter.length > 0){
            clearActive(filter, activeDefinitionExpression);
          }
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
    else
    {
      const filter = Object.assign({}, filterState);
      //New
      filter[FieldNames.customFilter.title] = filterTitleLocal ? filterTitleLocal.replaceAll('&lt;', '<').replaceAll('&gt;', '>') : '';
      const filterGuid = uuidv4();
      filter[FieldNames.customFilter.userCustomFiltersID] = filterGuid;
      filter[FieldNames.customFilter.sets] = filterState[FieldNames.customFilter.sets] ? filterState[FieldNames.customFilter.sets] : filterState.sets;
      filter[FieldNames.customFilter.mustMatchAll] = filterState[FieldNames.customFilter.mustMatchAll] !== undefined ? filterState[FieldNames.customFilter.mustMatchAll] : filterState.mustMatchAll;
      filter[FieldNames.customFilter.userID] = userInfo[FieldNames.userFields.userID];
      filter[FieldNames.customFilter.isActive] = FieldNames.yesNo.yes;
      filter[FieldNames.customFilter.dataset] = datasetSelection;
      activeDefinitionExpression = buildFilter(filter, false, userInfo);
      saveableActiveDefinitionExpression = buildFilter(filter, true, userInfo)
      filter[FieldNames.customFilter.expression] = saveableActiveDefinitionExpression;
      let modifiedSets = getModifiedSets(filter);

      //Create new CustomFilter for saving
      let saveAbleAttributes = {attributes: {}};
      saveAbleAttributes.attributes[FieldNames.customFilter.userID] = userInfo[FieldNames.userFields.userID];
      saveAbleAttributes.attributes[FieldNames.customFilter.mustMatchAll] = filter[FieldNames.customFilter.mustMatchAll] ? 'Yes' : 'No';
      saveAbleAttributes.attributes[FieldNames.customFilter.expression] = filter[FieldNames.customFilter.expression];
      saveAbleAttributes.attributes[FieldNames.customFilter.sets] = JSON.stringify(modifiedSets); 
      saveAbleAttributes.attributes[FieldNames.customFilter.title] = filter[FieldNames.customFilter.title] ? filter[FieldNames.customFilter.title].replaceAll('<', '&lt;').replaceAll('>', '&gt;') : '';
      saveAbleAttributes.attributes[FieldNames.customFilter.isActive] = FieldNames.yesNo.yes;
      saveAbleAttributes.attributes[FieldNames.customFilter.userCustomFiltersID] = filterGuid;
      saveAbleAttributes.attributes[FieldNames.customFilter.usersJoinId] = userInfo[FieldNames.userFields.id];
      saveAbleAttributes.attributes[FieldNames.customFilter.dataset] = datasetSelection;

      CustomFilterService.addCustomFilter(saveAbleAttributes).then(response => {
        if(response && response.addResults && response.addResults.length > 0 && response.addResults[0].success){
          //Need to get the objectID before adding to the orig
          //Deep clone to not copy reference so that when we update it re-renders
          const tempItems = JSON.parse(JSON.stringify(customSortedFilterListItems));
          filter[FieldNames.customFilter.objectID] = response.addResults[0].objectId;
          tempItems.push(filter);
          clearActive(filter, activeDefinitionExpression);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
    onClose();
  }

  const handleFilterChanged = (filterExpr, currentVal) => {
    //filterExpr is the string of the expression
    //currentVal is the entire filter
    setFilterState(currentVal);
  };

  const getDefaultFilterName = () => {  
    var filterLength = customSortedFilterListItems.length + 1;
    var concatFilterName = "Custom Filter " + filterLength.toString();
    return concatFilterName;
  }

  useEffect(() => { 
    if(!filterTitleLocal && open){
      var defaultName = getDefaultFilterName();
      setFilterTitleLocal(defaultName);
    }
  },[open]);

  const handleRadioFilterChange = (event) => {
    setDatasetSelection(event.target.value);
  }
  
  const actions = () => { 
    return (
      <div>
        <Button
          className="reporting-print-btn"
          variant="contained" color="primary"
          disabled={!filterTitleLocal}
          onClick={saveFilter}>Save</Button>
        <Button
          className="reporting-print-btn"
          onClick={onClose}>Close</Button>       
      </div>
    );
  }

    //nukeFilters();
    return (
      <Dialog
        style={{ minWidth: 550 }}
        onClose={onClose}
        fullScreen
        TransitionComponent={Transition}
        open={open}
        className="dialog-modal">
        <AppBar sx={{ position: 'relative' }} style={{backgroundColor: ColorConstants.BlueAlien}}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon style={{ fill: ColorConstants.WhiteAlien }} />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div" style={{ color: ColorConstants.WhiteAlien }}>
              Create Custom Filter
            </Typography>
          </Toolbar>
        </AppBar>
        <div style={{ backgroundColor: ColorConstants.LightGrayAlien }}>
          <Paper style={{ padding: 16, backgroundColor: ColorConstants.LightGrayAlien }}>
            <Paper elevation={6} style={{ padding: 20 }}>
              <Box p={1}>
                <div style={{display: "flex", alignItems: "stretch", flexDirection: "row", marginBottom: "22px"}}>
                  Filter Name: <BasicTextField
                              styleOverride={{ marginLeft: "15px", marginTop: "-6px", width: "40%" }}
                              value={filterTitleLocal}
                              setValue={setFilterTitleLocal}
                              isError={!filterTitleLocal} // This applies the error state if filterName is blank
                              helperText={!filterTitleLocal ? 'This field is required' : ''} // Optional: Display a helper text when the field is blank
                            />
                  {
                  isUserPostfire && isUserFuels ?
                    <RadioGroup
                      row
                      aria-labelledby="filter-controlled-radio-buttons-group"
                      name="filter-controlled-radio-buttons-group"
                      value={datasetSelection}
                      onChange={handleRadioFilterChange}
                      style={{display: 'flex', justifyContent: 'center', minWidth: 500}}
                    >
                        <FormControlLabel sx={{marginRight: "2vw"}} value={FieldNames.datasets.fuels} control={<Radio />} label={FieldNames.datasetLabels.fuels} />
                        <FormControlLabel sx={{marginRight: "2vw"}} value={FieldNames.datasets.postfire} control={<Radio />} label={FieldNames.datasetLabels.postfire} />
                        <FormControlLabel sx={{marginRight: "2vw"}} value={FieldNames.datasets.both} control={<Radio />} label={FieldNames.datasetLabels.both} />
                    </RadioGroup>
                  : null
                }
                </div>
                <ArcgisFilter
                  filterForEdit={filterForEdit}
                  fields={fields}
                  disableUnique={true}
                  onChange={handleFilterChanged} 
                  userInfo={userInfo}
                  filterTitleLocal={filterTitleLocal}
                  open={open}
                  datasetSelection={datasetSelection}
                />
              </Box>
            </Paper>
          </Paper>
        </div>
        <DialogActions>
          {actions()}
        </DialogActions>
      </Dialog>
    )
  
}